import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { post } from './client';
import type { ExerciseGuideItem, ExerciseGuideOption } from './model';

export interface ExerciseGuideListParams {
  keyword?: string;
  status?: string;
}

// 新增时需要编码；更新时编码不可改，服务端忽略
export type ExerciseGuideWriteParams = Omit<
  ExerciseGuideItem,
  'id' | 'createdAt' | 'updatedAt'
>;

export const exerciseGuideKeys = {
  all: ['exercise-guide'] as const,
  list: (params: ExerciseGuideListParams) =>
    [...exerciseGuideKeys.all, 'list', params] as const,
  options: () => [...exerciseGuideKeys.all, 'options'] as const,
};

export const listExerciseGuides = (params: ExerciseGuideListParams) =>
  post<ExerciseGuideItem[]>('/exercise-guide/list', params);

// 上架中的运动类型，处方编辑器下拉用
export const listExerciseGuideOptions = () =>
  post<ExerciseGuideOption[]>('/exercise-guide/options', {});

export const addExerciseGuide = (data: ExerciseGuideWriteParams) =>
  post<void>('/exercise-guide/add', data);

export const updateExerciseGuide = (
  data: ExerciseGuideWriteParams & { id: string },
) => post<void>('/exercise-guide/update', data);

export const deleteExerciseGuide = (id: string) =>
  post<void>('/exercise-guide/del', { id });

// 上架/下架，只改状态不重写整行
export const updateExerciseGuideStatus = (data: {
  id: string;
  status: 'active' | 'inactive';
}) => post<void>('/exercise-guide/status', data);

export const uploadExerciseGuideCover = (file: File) => {
  const formData = new FormData();
  formData.append('cover', file);
  return post<string>('/exercise-guide/cover/upload', formData);
};

export const useExerciseGuideListQuery = (params: ExerciseGuideListParams) =>
  useQuery({
    queryKey: exerciseGuideKeys.list(params),
    queryFn: () => listExerciseGuides(params),
  });

export const useExerciseGuideOptionsQuery = () =>
  useQuery({
    queryKey: exerciseGuideKeys.options(),
    queryFn: listExerciseGuideOptions,
    staleTime: 5 * 60 * 1000,
  });

export const useExerciseGuideAddMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addExerciseGuide,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: exerciseGuideKeys.all }),
  });
};

export const useExerciseGuideUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateExerciseGuide,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: exerciseGuideKeys.all }),
  });
};

export const useExerciseGuideDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteExerciseGuide,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: exerciseGuideKeys.all }),
  });
};

export const useExerciseGuideStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateExerciseGuideStatus,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: exerciseGuideKeys.all }),
  });
};

export const useExerciseGuideCoverUploadMutation = () =>
  useMutation({
    mutationFn: uploadExerciseGuideCover,
  });
